import type { PerformanceStatus } from '../types/dashboard';
import type { TrendMetric } from './metricConfig';

interface Band {
  below: number;
  priority: number;
  unit: 'relative' | 'pp';
}

/** Documented, deterministic comparison bands vs the network average. */
export const STATUS_BANDS: Record<TrendMetric, Band> = {
  members: { below: 0.1, priority: 0.25, unit: 'relative' },
  registrations: { below: 0.1, priority: 0.25, unit: 'relative' },
  revenue: { below: 0.1, priority: 0.25, unit: 'relative' },
  conversion: { below: 0.03, priority: 0.06, unit: 'pp' },
  regsPerMember: { below: 0.05, priority: 0.12, unit: 'relative' },
};

export function performanceStatus(metric: TrendMetric, gap: number, monthsSinceLaunch: number): PerformanceStatus {
  if (monthsSinceLaunch < 12) return 'Ramping';
  const b = STATUS_BANDS[metric];
  if (gap <= -b.priority) return 'Priority review';
  if (gap <= -b.below) return 'Below network';
  if (gap >= b.below) return 'Above network';
  return 'On par';
}

export function statusRules(metric: TrendMetric): { status: PerformanceStatus; rule: string }[] {
  const b = STATUS_BANDS[metric];
  const f = (v: number) => (b.unit === 'pp' ? `${Math.round(v * 100)} pp` : `${Math.round(v * 100)}%`);
  return [
    { status: 'Priority review', rule: `${f(b.priority)} or more below network avg` },
    { status: 'Below network', rule: `${f(b.below)}–${f(b.priority)} below` },
    { status: 'On par', rule: `within ±${f(b.below)}` },
    { status: 'Above network', rule: `${f(b.below)} or more above` },
    { status: 'Ramping', rule: 'launched < 12 months before range end' },
  ];
}

export const isBelowStatus = (s: PerformanceStatus) => s === 'Below network' || s === 'Priority review';
